/**
 * Guest Cart Operations Utility
 *
 * Provides reusable functions for guest cart operations across components.
 * Handles both normal products and products with choice options.
 */

import {
  addGuestCartItem,
  updateGuestCartItem,
  removeGuestCartItem,
  findExistingGuestCartItem,
  getGuestCartCount,
  getAllGuestCartItems,
  clearGuestCart,
  initGuestCartDB,
} from "./guestCartDB";

/**
 * Add item to guest cart with proper handling for choice options
 * @param {Object} params - The parameters for adding item to cart
 * @param {number} params.productId - Product ID
 * @param {number} params.quantity - Quantity to add
 * @param {Array} params.choiceOptions - Choice options array (optional)
 * @param {Array} params.addons - Addons array (optional)
 * @param {Object} params.productDetails - Product details object
 * @param {Function} params.dispatch - Auth context dispatch function
 * @param {Function} params.showSnackbar - Snackbar function for notifications
 * @returns {Promise<boolean>} - Success status
 */
export const addToGuestCart = async ({
  productId,
  quantity = 1,
  choiceOptions = {},
  addons = [],
  productDetails,
  dispatch,
  showSnackbar,
}) => {
  try {
    // Validate required parameters
    if (!productId || !productDetails) {
      throw new Error("Product ID and product details are required");
    }

    if (!dispatch || typeof dispatch !== "function") {
      throw new Error("Dispatch function is required");
    }

    // Check if item with same specifications already exists
    // Only check for existing items if there are NO choice options
    // Products with choice options should always be added as separate items
    const hasChoiceOptions =
      choiceOptions &&
      ((Array.isArray(choiceOptions) && choiceOptions.length > 0) ||
        (typeof choiceOptions === "object" &&
          Object.keys(choiceOptions).length > 0));

    let existingItem = null;
    if (!hasChoiceOptions) {
      existingItem = await findExistingGuestCartItem(
        productId,
        productDetails.Combination_Id ||
          productDetails.Default_Combination?.Combination_Id,
        {} // Empty choice options for products without choices
      );
    }

    if (existingItem && !hasChoiceOptions) {
      // Update existing item quantity (only for products without choice options)
      const newQuantity = existingItem.Units + quantity;
      
      // CRITICAL FIX: Ensure we use the correct Unit Price for calculation
      // Check if Price_Per_Unit looks like a Total Price (significantly higher than productDetails price) or equals previous Total_Price
      // This is a safety mechanism against compounded errors
      let safeUnitPrice = existingItem.Price_Per_Unit;
      
      if (productDetails && productDetails.Total_Selling_Price) {
         // Trust the incoming product details most if available
         safeUnitPrice = productDetails.Total_Selling_Price;
      } else if (existingItem.Inventory && existingItem.Inventory.Total_Selling_Price) {
         // Trust the stored Inventory master data
         safeUnitPrice = existingItem.Inventory.Total_Selling_Price;
      }
      
      const newTotalPrice = newQuantity * safeUnitPrice;

      await updateGuestCartItem(existingItem.id, {
        Units: newQuantity,
        Total_Price: newTotalPrice,
        Price_Per_Unit: safeUnitPrice // Enforce correct unit price
      });

      // Update context state
      dispatch({
        type: "UPDATE_GUEST_ITEM",
        payload: {
          id: existingItem.id,
          Units: newQuantity,
          Total_Price: newTotalPrice,
          Price_Per_Unit: safeUnitPrice
        },
      });

      // Update cart count in context
      const newCount = await getGuestCartCount();
      dispatch({
        type: "UPDATE_GUEST_CART_COUNT",
        payload: newCount,
      });

      if (showSnackbar) {
        showSnackbar(`Updated quantity in cart (${newQuantity})`, "success");
      }
    } else {
      // Add new item - match createGuestCartItem parameters
      const unitPrice = parseFloat(productDetails.Total_Selling_Price || 0);
      
      const cartItem = {
        productId,
        combinationId: productDetails.Combination_Id,
        units: quantity,
        pricePerUnit: unitPrice,
        choiceOptions: choiceOptions, // Store the full choice options array for display
        selectedAddons: addons,
        isSaveForLater: "0",
        productDetails,
      };

      const newItemId = await addGuestCartItem(cartItem);

      // Update context state
      dispatch({
        type: "ADD_GUEST_ITEM",
        payload: {
          ...cartItem,
          id: newItemId,
        },
      });

      if (showSnackbar) {
        showSnackbar("Added to cart successfully!", "success");
      }
    }

    // Update cart count
    const newCount = await getGuestCartCount();
    dispatch({
      type: "UPDATE_GUEST_CART_COUNT",
      payload: newCount,
    });

    return true;
  } catch (error) {
    console.error("Error adding to guest cart:", error);
    if (showSnackbar) {
      showSnackbar("Failed to add item to cart", "error");
    }
    return false;
  }
};

/**
 * Remove item from guest cart
 * @param {string} itemId - Cart item ID to remove
 * @param {Function} dispatch - Auth context dispatch function
 * @param {Function} showSnackbar - Snackbar function for notifications
 * @returns {Promise<boolean>} - Success status
 */
export const removeFromGuestCart = async (itemId, dispatch, showSnackbar) => {
  try {
    if (!itemId || !dispatch) {
      throw new Error("Item ID and dispatch function are required");
    }

    await removeGuestCartItem(itemId);

    // Update context state
    dispatch({
      type: "REMOVE_GUEST_ITEM",
      payload: itemId,
    });

    // Update cart count
    const newCount = await getGuestCartCount();
    dispatch({
      type: "UPDATE_GUEST_CART_COUNT",
      payload: newCount,
    });

    if (showSnackbar) {
      showSnackbar("Removed from cart", "success");
    }

    return true;
  } catch (error) {
    console.error("Error removing from guest cart:", error);
    if (showSnackbar) {
      showSnackbar("Failed to remove item from cart", "error");
    }
    return false;
  }
};

/**
 * Update quantity of item in guest cart
 * @param {string} itemId - Cart item ID
 * @param {number} newQuantity - New quantity
 * @param {Function} dispatch - Auth context dispatch function
 * @param {Function} showSnackbar - Snackbar function for notifications
 * @returns {Promise<boolean>} - Success status
 */
export const updateGuestCartQuantity = async (
  itemId,
  newQuantity,
  dispatch,
  showSnackbar
) => {
  try {
    if (!itemId || !dispatch || newQuantity < 1) {
      throw new Error(
        "Valid item ID, quantity, and dispatch function are required"
      );
    }

    await updateGuestCartItem(itemId, { Units: newQuantity });

    // Update context state
    dispatch({
      type: "UPDATE_GUEST_ITEM",
      payload: {
        id: itemId,
        Units: newQuantity,
        quantity: newQuantity, // Ensure both are updated in context
      },
    });

    // Update cart count
    const newCount = await getGuestCartCount();
    dispatch({
      type: "UPDATE_GUEST_CART_COUNT",
      payload: newCount,
    });

    if (showSnackbar) {
      showSnackbar(`Quantity updated to ${newQuantity}`, "success");
    }

    return true;
  } catch (error) {
    console.error("Error updating guest cart quantity:", error);
    if (showSnackbar) {
      showSnackbar("Failed to update quantity", "error");
    }
    return false;
  }
};

/**
 * Format choice options for display
 * @param {Array} choiceOptions - Array of choice options
 * @returns {string} - Formatted choice options string
 */
export const formatChoiceOptionsDisplay = (choiceOptions = {}) => {
  if (!choiceOptions || Object.keys(choiceOptions).length === 0) {
    return "";
  }

  return Object.entries(choiceOptions)
    .map(([key, value]) => {
      if (key && value) {
        return `${key}: ${value}`;
      }
      return "";
    })
    .filter(Boolean)
    .join(", ");
};

/**
 * Format addons for display
 * @param {Array} addons - Array of addons
 * @returns {string} - Formatted addons string
 */
export const formatAddonsDisplay = (addons = []) => {
  if (!addons || addons.length === 0) {
    return "";
  }

  return addons
    .map((addon) => addon.name || addon.addonName)
    .filter(Boolean)
    .join(", ");
};

/**
 * Calculate total price for a guest cart item including choice options and addons
 * @param {Object} item - Cart item object
 * @returns {number} - Total price
 */
export const calculateGuestCartItemPrice = (item) => {
  if (!item || !item.productDetails) {
    return 0;
  }

  let basePrice = parseFloat(item.productDetails.price || 0);

  // Note: Choice option prices are already included in the base product price
  // No additional price calculation needed for choice options in this simple format

  // Add addon prices
  if (item.addons && item.addons.length > 0) {
    const addonPrice = item.addons.reduce((total, addon) => {
      return total + parseFloat(addon.price || addon.addonPrice || 0);
    }, 0);
    basePrice += addonPrice;
  }

  return basePrice * (item.quantity || 1);
};

/**
 * Get formatted item display for cart
 * @param {Object} item - Cart item object
 * @returns {Object} - Formatted item display data
 */
export const getGuestCartItemDisplay = (item) => {
  // Safe Price Lookup Logic to prevent Total Price being used as Unit Price
  let safeUnitPrice = parseFloat(item.Price_Per_Unit || 0);
  
  // If we have master inventory data, check if it contradicts the stored unit price
  // This helps recover from cases where 'Price_Per_Unit' became 'Total_Price' in the DB
  const inventoryPrice = parseFloat(item.Inventory?.Total_Selling_Price || 0);
  const detailsPrice = parseFloat(item.productDetails?.Inventory?.Total_Selling_Price || 0);
  
  if (inventoryPrice > 0 && inventoryPrice < safeUnitPrice) {
       // If stored price is larger than master price, it's likely corrupted (e.g. holds total)
       safeUnitPrice = inventoryPrice;
  } else if (detailsPrice > 0 && detailsPrice < safeUnitPrice) {
       safeUnitPrice = detailsPrice;
  } else if (safeUnitPrice === 0) {
      // Fallbacks if price is 0
      safeUnitPrice = inventoryPrice || detailsPrice || 0;
  }

  // Guest cart items now have the same structure as authenticated cart items
  // So we can return them directly with minimal processing
  return {
    // Return the item as-is since it already matches authenticated cart structure
    ...item,

    // Ensure these fields are present and properly formatted for compatibility
    id: item.id || item.Cart_Id,
    Cart_Id: item.Cart_Id || item.id,
    productId: item.Product_Id,
    productName: item.Product_Full_Name,
    quantity: item.Units,
    
    // Use the safe resolved unit price
    unitPrice: safeUnitPrice,
    totalPrice: parseFloat(item.Units * safeUnitPrice),

    // Ensure critical display fields are set
    Product_Full_Name: item.Product_Full_Name,
    Short_Description: item.Short_Description || "",
    Combination_Text: item.Combination_Text || "",
    Units: item.Units,

    // Critical: Ensure 'price' field is set for cart display
    price: safeUnitPrice,
    Price_Per_Unit: safeUnitPrice,
    Total_Price: parseFloat(item.Units * safeUnitPrice),

    // Choice options - now stored in the correct format for guest cart display
    Choice_Options: item.Choice_Options || {},
    choiceOptions: item.display_choice_text || "", // Keep for backward compatibility
    addons: "", // TODO: Implement addon display if needed

    // Image handling - match authenticated cart logic exactly
    image: (() => {
      // First try the stored image field
      if (item.image) return item.image;

      // Then try to get from Media array
      if (Array.isArray(item.Media) && item.Media.length > 0) {
        const imageObj = item.Media.find(
          (m) => m.Media_File_Type === "image" && m.Is_Deleted === "0"
        );
        if (imageObj && imageObj.Media_File_Path)
          return imageObj.Media_File_Path;
      }

      // Return null - AddToCart component will handle fallback to loadImage
      return null;
    })(),
    imageUrl: (() => {
      // Same logic for imageUrl
      if (item.image) return item.image;

      if (Array.isArray(item.Media) && item.Media.length > 0) {
        const imageObj = item.Media.find(
          (m) => m.Media_File_Type === "image" && m.Is_Deleted === "0"
        );
        if (imageObj && imageObj.Media_File_Path)
          return imageObj.Media_File_Path;
      }

      return null;
    })(),
    imageAlt: item.Product_Full_Name,

    // Add Default_Media for ProductCard compatibility
    Default_Media:
      item.Default_Media ||
      (Array.isArray(item.Media)
        ? item.Media.filter(
            (m) => m.Media_File_Type === "image" && m.Is_Deleted === "0"
          ).map((m) => m.Media_File_Path)
        : []),
    Product_Images: item.Product_Images || [],

    // Add Product_Image_1 for GuestOrderSummary compatibility
    Product_Image_1: (() => {
      // Use stored Product_Image_1 if available
      if (item.Product_Image_1) return item.Product_Image_1;

      // Fallback to first available image from arrays
      if (
        item.Product_Images &&
        Array.isArray(item.Product_Images) &&
        item.Product_Images.length > 0
      ) {
        return item.Product_Images[0];
      }
      if (
        item.Default_Media &&
        Array.isArray(item.Default_Media) &&
        item.Default_Media.length > 0
      ) {
        return item.Default_Media[0];
      }

      // Final fallback to image field
      return item.image || null;
    })(),

    // Status flags
    hasChoiceOptions: Boolean(
      item.Choice_Options && Object.keys(item.Choice_Options).length > 0
    ),
    hasAddons: Boolean(item.Selected_Addons && item.Selected_Addons.length > 0),
  };
};

/**
 * Toggle Save for Later status for a guest cart item
 * @param {string} cartId - Cart item ID
 * @param {Function} dispatch - Auth context dispatch function
 * @param {Function} showSnackbar - Snackbar function for notifications
 * @returns {Promise<boolean>} - Success status
 */
export const toggleGuestCartSaveForLater = async ({
  cartId,
  dispatch,
  showSnackbar,
}) => {
  try {
    // Get current item
    const db = await initGuestCartDB();
    const transaction = db.transaction(["guestCart"], "readwrite");
    const store = transaction.objectStore("guestCart");

    const getRequest = store.get(cartId);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (!item) {
          reject(new Error("Cart item not found"));
          return;
        }

        // Toggle Save for Later status
        const newStatus = item.Is_Savefor_Later === "1" ? "0" : "1";
        item.Is_Savefor_Later = newStatus;
        item.Modified_On = new Date().toISOString();

        // Update the item
        const updateRequest = store.put(item);

        updateRequest.onsuccess = () => {
          // Update context state
          dispatch({
            type: "UPDATE_GUEST_CART_ITEM",
            payload: {
              id: cartId,
              updates: { Is_Savefor_Later: newStatus },
            },
          });

          if (showSnackbar) {
            const message =
              newStatus === "1"
                ? "Item saved for later"
                : "Item moved back to cart";
            showSnackbar(message, "success");
          }

          resolve(true);
        };

        updateRequest.onerror = () => reject(updateRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error("Error toggling save for later:", error);
    if (showSnackbar) {
      showSnackbar("Failed to update item", "error");
    }
    return false;
  }
};

// Re-export functions from guestCartDB for easier importing
export {
  getGuestCartCount,
  getAllGuestCartItems,
  clearGuestCart,
} from "./guestCartDB";

/**
 * Navigate to appropriate checkout based on authentication status
 * @param {boolean} isAuthenticated - User authentication status
 * @param {Function} navigate - React Router navigate function
 */
export const navigateToCheckout = (isAuthenticated, navigate) => {
  if (isAuthenticated) {
    navigate("/AfterProceedToCheckout");
  } else {
    navigate("/GuestCheckout");
  }
};
